import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAudio } from "@/context/AudioContext";
import { HERO_VIDEO, AUDIO_TRACK } from "@/lib/media";

/**
 * Intro-scherm: één klik om binnen te komen.
 *
 * Waarom dit bestaat: browsers weigeren geluid af te spelen voordat de
 * bezoeker iets heeft aangeklikt. In plaats van dat te omzeilen met
 * verborgen luisteraars, maken we er een bewuste stap van — die klik is
 * meteen de toestemming, dus de muziek start gegarandeerd.
 *
 * Eén keer per sessie: wie doorklikt naar een andere pagina en terugkomt,
 * krijgt het scherm niet opnieuw.
 */
const SEEN_KEY = "daniq:entered";

export const EntryScreen = () => {
  const audio = useAudio();
  const [open, setOpen] = useState(() => {
    try {
      return sessionStorage.getItem(SEEN_KEY) !== "1";
    } catch {
      return true; // privémodus e.d. — dan gewoon tonen
    }
  });

  // Zwaar materiaal (hero-video + muziek) alvast binnenhalen terwijl het
  // intro-scherm in beeld staat. De bytes belanden in de HTTP-cache, dus na
  // Enter draait de video meteen in plaats van eerst nog te moeten laden.
  //
  // De voortgangsbalk informeert, maar blokkeert niet: Enter blijft altijd
  // klikbaar. Iemand op een trage verbinding 11 MB laten afwachten is erger
  // dan het probleem dat we oplossen.
  const [progress, setProgress] = useState(0);
  const aborter = useRef(null);

  useEffect(() => {
    if (!open) return;
    const ac = new AbortController();
    aborter.current = ac;
    let cancelled = false;

    (async () => {
      const assets = [HERO_VIDEO, AUDIO_TRACK].filter(Boolean);
      const loaded = new Array(assets.length).fill(0);
      const totals = new Array(assets.length).fill(0);

      const tick = () => {
        const done = loaded.reduce((a, b) => a + b, 0);
        const all = totals.reduce((a, b) => a + b, 0);
        if (all > 0 && !cancelled) setProgress(Math.min(100, Math.round((done / all) * 100)));
      };

      await Promise.all(
        assets.map(async (url, i) => {
          try {
            const resp = await fetch(url, { signal: ac.signal });
            totals[i] = Number(resp.headers.get('content-length')) || 0;
            const reader = resp.body?.getReader();
            if (!reader) return;
            for (;;) {
              const { done, value } = await reader.read();
              if (done) break;
              loaded[i] += value.length;
              tick();
            }
          } catch {
            /* afgebroken of offline — niet fataal, de site werkt gewoon */
          }
        }),
      );
      if (!cancelled) setProgress(100);
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [open]);

  const enter = (withSound) => {
    if (withSound) audio?.play();
    try {
      sessionStorage.setItem(SEEN_KEY, "1");
    } catch {
      /* niet fataal */
    }
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="entry-screen"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
          className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-ink-950 px-6"
        >
          {/* zachte gloed, zelfde taal als de hero */}
          <div className="pointer-events-none absolute -bottom-32 left-1/2 -translate-x-1/2 h-[420px] w-[420px] rounded-full bg-magenta/20 blur-[130px] animate-pulse-glow" />
          <div className="pointer-events-none absolute -top-24 right-0 h-[300px] w-[300px] rounded-full bg-violet/20 blur-[120px]" />

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.76, 0, 0.24, 1] }}
            className="relative z-10 flex flex-col items-center text-center"
          >
            <h1 className="font-display leading-[0.85] text-white text-[19vw] sm:text-[13vw] lg:text-[9vw] tracking-[0.02em]">
              DANI<span className="text-magenta">Q</span>
            </h1>
            <p className="mt-3 text-[10px] sm:text-xs uppercase tracking-[0.45em] text-gold">
              DJ · Drummer · MC · Producer
            </p>

            {/* Voortgang: informatief, geen drempel. */}
            <div className="mt-12 w-[240px] sm:w-[300px]">
              <div className="h-px w-full bg-white/15">
                <motion.div
                  data-testid="entry-progress"
                  className="h-px bg-magenta"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ ease: "linear", duration: 0.25 }}
                />
              </div>
              <div className="mt-3 flex justify-between text-[9px] uppercase tracking-[0.3em] text-white/35">
                <span>{progress >= 100 ? "Gereed" : "Laden"}</span>
                <span>{progress}%</span>
              </div>
            </div>

            <button
              data-testid="entry-enter-btn"
              onClick={() => enter(true)}
              className="group relative mt-10 overflow-hidden rounded-full bg-magenta px-12 py-4 text-sm font-medium uppercase tracking-[0.25em] text-white glow-magenta transition-transform duration-300 hover:scale-[1.03]"
            >
              <span className="relative z-10">Enter</span>
              <span className="absolute inset-0 -translate-x-full bg-violet transition-transform duration-500 group-hover:translate-x-0" />
            </button>

            <button
              data-testid="entry-silent-btn"
              onClick={() => enter(false)}
              className="mt-5 text-[11px] uppercase tracking-[0.25em] text-white/40 hover:text-white/70 transition-colors duration-300"
            >
              Zonder geluid
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
